/**
 * Returns the current user's effective role for the active incident/OP.
 *
 * Overhead check-ins create a `user_ops` document with `role` set to the
 * ICS overhead role (IC, Ops, Logistics, etc.).  Field unit check-ins do not
 * create a `user_ops` document, so their effective role falls back to the
 * agency role on the user profile (`member` | `admin`).
 *
 * Pass `opRole.value` to `permissionsStore.can(actionKey, opRole.value)`.
 */
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { collection, query, where, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useOpsStore } from '@/stores/ops'

export function useCurrentOpRole() {
  const route = useRoute()
  const authStore = useAuthStore()
  const opsStore = useOpsStore()

  const opRole = ref<string | null | undefined>(undefined)
  let unsub: Unsubscribe | null = null

  const incidentId = computed(() => route.params.id as string | undefined)
  const opId = computed(() => opsStore.selectedOpId)

  function subscribe() {
    unsub?.()
    unsub = null

    const iid = incidentId.value
    const oid = opId.value
    const uid = authStore.currentUser?.uid

    if (!iid || !oid || !uid) {
      opRole.value = authStore.userProfile?.role ?? null
      return
    }

    const q = query(
      collection(db, 'incidents', iid, 'operational_periods', oid, 'user_ops'),
      where('user_id', '==', uid),
      where('active', '==', true),
    )
    unsub = onSnapshot(
      q,
      (snap) => {
        if (!snap.empty && snap.docs[0]) {
          opRole.value = (snap.docs[0].data() as { role?: string }).role ?? null
        } else {
          opRole.value = authStore.userProfile?.role ?? null
        }
      },
      () => {
        opRole.value = authStore.userProfile?.role ?? null
      },
    )
  }

  subscribe()
  watch([incidentId, opId, () => authStore.currentUser?.uid], subscribe)

  onUnmounted(() => {
    unsub?.()
  })

  return { opRole }
}
